/**
 * Tiện ích định dạng tiền tệ VND cho hiển thị BĐS.
 *
 * - formatCurrency: hiển thị đầy đủ (tỷ/triệu/đơn vị nhỏ).
 * - formatPropertyPrice: che bớt số (mask) trên card công khai để bảo mật giá.
 */
import { TEXT } from '../languages';

/** Format số tiền đầy đủ — tự chọn đơn vị tỷ/triệu hoặc Intl vi-VN */
const formatFullCurrency = (amount) => {
  const numericAmount = Number(amount || 0);
  const { currencyUnit, billionUnit, millionUnit } = TEXT.common;

  if (numericAmount >= 1_000_000_000) {
    const billionAmount = numericAmount / 1_000_000_000;
    const fractionDigits = numericAmount % 1_000_000_000 === 0 ? 0 : 1;

    return `${billionAmount.toFixed(fractionDigits)} ${billionUnit} ${currencyUnit}`;
  }

  if (numericAmount >= 1_000_000) {
    const millionAmount = numericAmount / 1_000_000;
    const fractionDigits = numericAmount % 1_000_000 === 0 ? 0 : 1;

    return `${millionAmount.toFixed(fractionDigits)} ${millionUnit} ${currencyUnit}`;
  }

  const formattedAmount = new Intl.NumberFormat('vi-VN', {
    maximumFractionDigits: 0,
  }).format(numericAmount);

  return `${formattedAmount} ${currencyUnit}`;
};

/** Che các chữ số sau số đầu tiên (ví dụ: 5.x tỷ → 5x) */
const maskIntegerRange = (amount) => {
  const integerAmount = Math.floor(amount);
  const amountText = String(integerAmount);

  if (integerAmount < 10) {
    return `${integerAmount}.x`;
  }

  return `${amountText.charAt(0)}${'x'.repeat(amountText.length - 1)}`;
};

/** Format giá BĐS công khai — mask phần lớn số để không lộ chính xác */
const formatMaskedPropertyPrice = (amount) => {
  const numericAmount = Number(amount || 0);
  const { billionUnit, millionUnit } = TEXT.common;

  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    return formatFullCurrency(amount);
  }

  if (numericAmount >= 1_000_000_000) {
    return `${maskIntegerRange(numericAmount / 1_000_000_000)} ${billionUnit}`;
  }

  if (numericAmount >= 1_000_000) {
    return `${maskIntegerRange(numericAmount / 1_000_000)} ${millionUnit}`;
  }

  return formatFullCurrency(amount);
};

/**
 * Format tiền đầy đủ (admin, hồ sơ, chi tiết admin).
 * @param {number|string} amount
 * @returns {string}
 */
export const formatCurrency = (amount) => formatFullCurrency(amount);

/**
 * Format giá BĐS trên card công khai (có mask).
 * @param {number|string} amount
 * @returns {string}
 */
export const formatPropertyPrice = (amount) => formatMaskedPropertyPrice(amount);
